// FrameResizeHandles.tsx
import React, { useCallback, useMemo } from "react";
import { FrameResizeState, ResizeHandleType } from "#types/types";
import { useKernel } from "@/kernel/Kernel";
import {
  useKernelStore,
} from "@/kernel/kernelStores";
import { selectActiveSessionProjectIdFromInventoryState } from '#lib/leasing/chat-session-project-focus';

type FrameResizeHandlesProps = {
  workspaceId: string;
  relativePositioning: boolean;
};

const DEFAULT_FRAME_RESIZE_STATE: FrameResizeState = {
  isResizing: false,
  handleType: null,
  startPos: null,
  initialRect: null,
  delta: { dx: 0, dy: 0 },
  workspaceId: null,
};

const HANDLES: ResizeHandleType[] = ['left', 'right', 'top', 'bottom', 'topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

const FrameResizeHandles = React.memo(({
    workspaceId,
    relativePositioning,
}: FrameResizeHandlesProps) => {
  const { kernel } = useKernel();
  const projectId = useKernelStore(selectActiveSessionProjectIdFromInventoryState);
  const zoomLevelFromStore = useKernelStore((state) =>
    projectId
      ? (() => {
          const runtime = state.inventory.Project.registry[projectId]?.runtime;
          const value = runtime?.zoomLevel;
          return typeof value === 'number' && Number.isFinite(value) ? value : 1;
        })()
      : 1
  );
  const workspaceFromStore = useKernelStore((state) =>
    state.inventory.StudioWorkspace.registry[workspaceId] ?? undefined
  );
  const effectiveZoomLevel = zoomLevelFromStore;
  const effectiveWorkspace = workspaceFromStore;
  const item = effectiveWorkspace;
  const frame = item?.frameRect;

  // Le workspace est réécrit à chaque mousemove pendant un redimensionnement (frameResizeState),
  // donc le capturer en dépendance recréerait les deux handlers à la fréquence du curseur.
  const resolveHandleTarget = useCallback(() => {
    const k = kernel.current;
    if (!k) return null;
    const workspace = k.getWorkspace(workspaceId);
    if (!workspace || workspace.locked) return null;
    const activeProjectId = selectActiveSessionProjectIdFromInventoryState({
      activeChatSessionId: k.getActiveChatSessionId(),
      inventory: k.getInventory(),
    });
    if (!activeProjectId) return null;
    return { kernelHandle: k, workspace, projectId: activeProjectId };
  }, [kernel, workspaceId]);

  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const handleType = e.currentTarget.dataset.handle as ResizeHandleType;
    e.stopPropagation();
    const target = resolveHandleTarget();
    if (!target) return;
    target.kernelHandle.logUserAction('Workspace Frame Autofit Handle', { workspace_id: target.workspace.id, handle: handleType });
    target.kernelHandle.fitWorkspaceFrameToContent(target.projectId, target.workspace.id, handleType);
  }, [resolveHandleTarget]);

  const getResizeHandleStyle = useCallback((handleType: ResizeHandleType): React.CSSProperties => {
    if (!frame) return {};

    const offsetX = relativePositioning ? 0 : frame.x;
    const offsetY = relativePositioning ? 0 : frame.y;

    // --- Common Calculations ---
    const cornerHandleThickness = 4 / effectiveZoomLevel;
    const cornerArcRadius = 25 / effectiveZoomLevel;
    const cornerRingDiameter = cornerArcRadius * 2;
    const sideHandleThickness = 6 / effectiveZoomLevel;

    // --- Base Styles ---
    // We now anchor everything at the top-left (0,0) of the parent
    // and use `transform` for all positioning. This avoids compounding
    // rounding errors between `top`/`left` and `transform`.
    const baseStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 101,
        pointerEvents: 'auto',
        boxSizing: 'border-box',
    };

    const handleColor = '#B3D9FF';

    const cornerRingStyle: React.CSSProperties = {
        ...baseStyle,
        width: cornerRingDiameter,
        height: cornerRingDiameter,
        backgroundColor: 'transparent',
        border: `${cornerHandleThickness}px solid ${handleColor}`,
        borderRadius: '50%',
    };

    const sideBaseStyle: React.CSSProperties = {
        ...baseStyle,
        backgroundColor: handleColor,
        borderRadius: `${2 / effectiveZoomLevel}px`,
    };

    // --- Handle-Specific Styles ---
    switch (handleType) {
      // Corners: Positioned so the center of the border aligns with the frame's edge.
      // This is achieved by shifting the handle by half its border thickness.
      case 'topLeft': {
          const x = offsetX - cornerHandleThickness / 2;
          const y = offsetY - cornerHandleThickness / 2;
          return {
              ...cornerRingStyle,
              cursor: 'nwse-resize',
              clipPath: 'inset(0 50% 50% 0)',
              transform: `translate(${x}px, ${y}px)`,
          };
      }
      case 'topRight': {
          const x = offsetX + frame.w - cornerRingDiameter + cornerHandleThickness / 2;
          const y = offsetY - cornerHandleThickness / 2;
          return {
              ...cornerRingStyle,
              cursor: 'nesw-resize',
              clipPath: 'inset(0 0 50% 50%)',
              transform: `translate(${x}px, ${y}px)`,
          };
      }
      case 'bottomLeft': {
          const x = offsetX - cornerHandleThickness / 2;
          const y = offsetY + frame.h - cornerRingDiameter + cornerHandleThickness / 2;
          return {
              ...cornerRingStyle,
              cursor: 'nesw-resize',
              clipPath: 'inset(50% 50% 0 0)',
              transform: `translate(${x}px, ${y}px)`,
          };
      }
      case 'bottomRight': {
          const x = offsetX + frame.w - cornerRingDiameter + cornerHandleThickness / 2;
          const y = offsetY + frame.h - cornerRingDiameter + cornerHandleThickness / 2;
          return {
              ...cornerRingStyle,
              cursor: 'nwse-resize',
              clipPath: 'inset(50% 0 0 50%)',
              transform: `translate(${x}px, ${y}px)`,
          };
      }

      // Sides: Positioned so the center of the handle aligns with the frame's edge.
      case 'left': {
        const handleHeight = frame.h * 0.1;
        const x = offsetX - sideHandleThickness / 2;
        const y = frame.y + (frame.h / 2) - (handleHeight / 2);
        return { ...sideBaseStyle, cursor: 'ew-resize', width: sideHandleThickness, height: handleHeight, transform: `translate(${x}px, ${y}px)` };
      }
      case 'right': {
        const handleHeight = frame.h * 0.1;
        const x = offsetX + frame.w - sideHandleThickness / 2;
        const y = frame.y + (frame.h / 2) - (handleHeight / 2);
        return { ...sideBaseStyle, cursor: 'ew-resize', width: sideHandleThickness, height: handleHeight, transform: `translate(${x}px, ${y}px)` };
      }
      case 'top': {
        const handleWidth = frame.w * 0.1;
        const x = frame.x + (frame.w / 2) - (handleWidth / 2);
        const y = offsetY - sideHandleThickness / 2;
        return { ...sideBaseStyle, cursor: 'ns-resize', width: handleWidth, height: sideHandleThickness, transform: `translate(${x}px, ${y}px)` };
      }
      case 'bottom': {
        const handleWidth = frame.w * 0.1;
        const x = frame.x + (frame.w / 2) - (handleWidth / 2);
        const y = offsetY + frame.h - sideHandleThickness / 2;
        return { ...sideBaseStyle, cursor: 'ns-resize', width: handleWidth, height: sideHandleThickness, transform: `translate(${x}px, ${y}px)` };
      }
      default: return {};
    }
  }, [frame, effectiveZoomLevel, relativePositioning]);

  const startWorkspaceResize = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const target = resolveHandleTarget();
    if (!target || !target.workspace.frameRect) return;
    const handleType = e.currentTarget.dataset.handle as ResizeHandleType;

    e.stopPropagation();
    target.kernelHandle.setWorkspace(target.workspace.id, (workspace) => ({
      ...workspace,
      frameResizeState: {
        ...DEFAULT_FRAME_RESIZE_STATE,
        isResizing: true,
        handleType: handleType,
        startPos: { x: e.clientX, y: e.clientY },
        initialRect: target.workspace.frameRect,
        workspaceId: target.workspace.id,
      },
    }), target.projectId);
  }, [resolveHandleTarget]);

  const handleStyles = useMemo(
    () => HANDLES.map(getResizeHandleStyle),
    [getResizeHandleStyle]
  );

  if (!item || !frame) {
    return null;
  }

  return (
    <>
      {HANDLES.map((handle, index) => (
        <div
          key={`resize-${item.id}-${handle}`}
          data-handle={handle}
          style={handleStyles[index]}
          onMouseDown={startWorkspaceResize}
          onDoubleClick={handleDoubleClick}
        />
      ))}
    </>
  );
});

FrameResizeHandles.displayName = 'FrameResizeHandles';
export default FrameResizeHandles;

